import {
  Scene,
  OrthographicCamera,
  PerspectiveCamera,
  WebGLRenderer,
  Color,
} from "three";
import { isMobile } from "./util/mobile";

export interface Viewport {
  aspect: number;
  width: number;
  height: number;
}

export interface SceneContext {
  renderer: WebGLRenderer;
  scene: Scene;
  /** 既定の正射影カメラ。 */
  camera: OrthographicCamera;
  /** 透視投影カメラ (ortho と同じ位置・near/far、z=5 で同じ画角になる fov)。 */
  perspectiveCamera: PerspectiveCamera;
  viewport: Viewport;
  /** resize リスナー解除 + renderer.dispose。ロゴ unmount 時に呼ぶ。 */
  dispose: () => void;
}

export function createScene(canvas: HTMLCanvasElement): SceneContext {
  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: "high-performance",
    // 動画書き出しで canvas → 2D context への drawImage が空にならないように。
    // ブラウザのコンポジット後にバッファをクリアしない設定。
    preserveDrawingBuffer: true,
  });
  renderer.setClearColor(new Color(0xffffff), 1);
  // モバイルは DPR を 1.5 に抑える。iPhone 等の DPR=3 端末では本来の 1/4 の
  // フラグメント負荷になり、GPU の塗り潰し量が劇的に減る (60fps を保ちやすい)。
  // 視覚的な粗さは小画面サイズではほぼ気にならない。PC は従来通り 2 まで許可。
  const dprCap = isMobile() ? 1.5 : 2;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, dprCap));

  const scene = new Scene();

  // Orthographic camera: 横は [-aspect, aspect]、縦は [-1, 1]。
  // OrbitControls で回せるよう、カメラを z=5 に離して near/far を広めに取る。
  const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 20);
  camera.position.set(0, 0, 5);
  camera.lookAt(0, 0, 0);

  // Perspective camera: z=5 で正射影と同じ縦画角 (半高 1) になる fov を選ぶ。
  //   半高 = 距離 × tan(fov/2) = 1 → fov = 2·atan(1/5) ≈ 22.62°
  // 切り替えても被写体サイズはほぼ揃い、立方体の奥行きで遠近感が出る。
  const PERSP_FOV = (2 * Math.atan(1 / 5) * 180) / Math.PI;
  const perspectiveCamera = new PerspectiveCamera(PERSP_FOV, 1, 0.1, 20);
  perspectiveCamera.position.set(0, 0, 5);
  perspectiveCamera.lookAt(0, 0, 0);

  const viewport: Viewport = { aspect: 1, width: 1, height: 1 };

  const resize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const aspect = w / h;
    renderer.setSize(w, h, false);
    camera.left = -aspect;
    camera.right = aspect;
    camera.top = 1;
    camera.bottom = -1;
    camera.updateProjectionMatrix();
    perspectiveCamera.aspect = aspect;
    perspectiveCamera.updateProjectionMatrix();
    viewport.aspect = aspect;
    viewport.width = w;
    viewport.height = h;
  };

  resize();
  window.addEventListener("resize", resize);

  const dispose = (): void => {
    window.removeEventListener("resize", resize);
    renderer.dispose();
  };

  return { renderer, scene, camera, perspectiveCamera, viewport, dispose };
}
